import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// PDF styles
const pdfStyles = StyleSheet.create({
  portraitPage: {
    padding: 40,
    fontFamily: 'Helvetica',
  },
  landscapePage: {
    fontFamily: 'Helvetica',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    height: 60,
    padding: '12px 40px',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    marginBottom: 0,
  },
  headerLogo: {
    width: 100,
    height: 50,
    objectFit: 'contain',
  },
  content: {
    flex: 1,
    padding: '10px 40px',
  },
  footer: {
    height: 50,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  confidential: {
    fontSize: 11,
    color: '#546e7a',
    textAlign: 'center',
  },
  pageNumber: {
    position: 'absolute',
    bottom: 5,
    right: 0,
    fontSize: 11,
    color: '#546e7a',
  },
  title: {
    fontSize: 16,
    marginBottom: 8,
    color: '#1a237e',
  },
  subtitle: {
    fontSize: 12,
    marginBottom: 12,
    color: '#455a64',
  },
  metadata: {
    fontSize: 10,
    marginBottom: 4,
    color: '#546e7a',
  },
  chartsSection: {
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  chartContainer: {
    width: '48%',
  },
  chartTitle: {
    fontSize: 12,
    marginBottom: 8,
    textAlign: 'center',
    color: '#1976d2',
  },
  chart: {
    width: '100%',
    height: 180,
  },
  gaugeChart: {
    width: '100%',
    height: 180,
  },
  section: {
    marginBottom: 30,
    padding: 20,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 16,
    color: '#1976d2',
  },
  table: {
    width: '100%',
    marginTop: 20,
  },
  tableHeader: {
    borderBottomWidth: 2,
    borderBottomColor: '#e0e0e0',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    minHeight: 30,
    alignItems: 'center',
  },
  tableCell: {
    padding: 12,
    fontSize: 9,
    textAlign: 'left',
  },
  ref: {
    width: '8%',
  },
  domain: {
    width: '12%',
  },
  control: {
    width: '25%',
  },
  assessment: {
    width: '15%',
  },
  comments: {
    width: '20%',
  },
  countermeasures: {
    width: '20%',
  },
  assessmentRed: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: 6,
    fontSize: 9,
    textAlign: 'center',
  },
  assessmentYellow: {
    backgroundColor: '#fff3e0',
    color: '#ef6c00',
    padding: 6,
    fontSize: 9,
    textAlign: 'center',
  },
  assessmentGreen: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: 6,
    fontSize: 9,
    textAlign: 'center',
  },
  assessmentDefault: {
    backgroundColor: '#f5f5f5',
    color: '#37474f',
    padding: 6,
    fontSize: 9,
    textAlign: 'center',
  },
  summaryRow: {
    backgroundColor: '#f8f9fa',
  },
  summaryScore: {
    color: '#1565c0',
    fontSize: 10,
    textAlign: 'center',
  }
});

const AssessmentReport = ({ data, customer, framework, startDate, finishDate, genScore, customerLogo, radarImage, gaugeImage }) => {
  // Create simple base64 image sources
  const createImageSource = (base64Data) => {
    if (!base64Data) return null;
    
    // For @react-pdf/renderer, we need to use a special format
    return { uri: `data:image/png;base64,${base64Data}`, cache: true };
  };
  
  // Create image sources
  const radarSource = createImageSource(radarImage);
  const gaugeSource = createImageSource(gaugeImage);
  const logoSource = createImageSource(customerLogo);

  return (
    <Document>
      {/* First page with summary and charts */}
      <Page 
        size="A4" 
        orientation="landscape" 
        style={pdfStyles.landscapePage}
      >
        {/* Fixed header that appears on every page */}
        <View style={pdfStyles.header} fixed>
          {logoSource && (
            <Image 
              src={logoSource}
              style={pdfStyles.headerLogo} 
            />
          )}
        </View>

        {/* Main content with proper margins to avoid overlapping with header/footer */}
        <View style={pdfStyles.content}>
          {/* Summary Section */}
          <View style={{ marginBottom: 30 }}>
            <Text style={pdfStyles.title}>Gap Analysis Report</Text>
            <Text style={pdfStyles.subtitle}>{customer} - {framework}</Text>
            <Text style={pdfStyles.metadata}>Start Date: {startDate}</Text>
            <Text style={pdfStyles.metadata}>Finish Date: {finishDate}</Text>
            <Text style={pdfStyles.metadata}>Overall Compliance: {Math.round(genScore * 100)}%</Text>

            <View style={pdfStyles.chartsSection}>
              <View style={pdfStyles.chartContainer}>
                <Text style={pdfStyles.chartTitle}>Cumplimiento - Funciones de Ciberseguridad</Text>
                {radarSource && (
                  <Image 
                    src={radarSource}
                    style={pdfStyles.chart} 
                  />
                )}
              </View>
              <View style={pdfStyles.chartContainer}>
                <Text style={pdfStyles.chartTitle}>Nivel de Cumplimiento General</Text>
                {gaugeSource && (
                  <Image 
                    src={gaugeSource}
                    style={pdfStyles.gaugeChart} 
                  />
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Fixed footer that appears on every page */}
        <View style={pdfStyles.footer} fixed>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={pdfStyles.confidential}>CONFIDENCIAL</Text>
          </View>
          <Text 
            style={pdfStyles.pageNumber} 
            render={({ pageNumber, totalPages }) => (
              `${pageNumber} / ${totalPages}`
            )} 
          />
        </View>
      </Page>

      {/* Second page with assessment results table */}
      <Page 
        size="A4" 
        orientation="landscape" 
        style={pdfStyles.landscapePage}
      >
        <View style={pdfStyles.header} fixed>
          {logoSource && (
            <Image 
              src={logoSource}
              style={pdfStyles.headerLogo} 
            />
          )}
        </View>

        <View style={pdfStyles.content}>
          <View style={pdfStyles.section}>
            <Text style={pdfStyles.sectionTitle}>Assessment Results</Text>
            <View style={pdfStyles.table}>
              <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]} fixed>
                <Text style={[pdfStyles.tableCell, pdfStyles.ref]}>Ref</Text>
                <Text style={[pdfStyles.tableCell, pdfStyles.domain]}>Domain</Text>
                <Text style={[pdfStyles.tableCell, pdfStyles.control]}>Control</Text>
                <Text style={[pdfStyles.tableCell, pdfStyles.assessment]}>Assessment</Text>
                <Text style={[pdfStyles.tableCell, pdfStyles.comments]}>Comments</Text>
                <Text style={[pdfStyles.tableCell, pdfStyles.countermeasures]}>Countermeasures</Text>
              </View>
              {data.map((item, index) => (
                <View key={index} style={[pdfStyles.tableRow, item.isSummary && pdfStyles.summaryRow]}>
                  <Text style={[pdfStyles.tableCell, pdfStyles.ref]}>{item.ref}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.domain]}>{item.domain}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.control]}>{item.description}</Text>
                  <View style={[pdfStyles.tableCell, pdfStyles.assessment]}>
                    {item.isSummary ? (
                      <Text style={pdfStyles.summaryScore}>{`${item.value}%`}</Text>
                    ) : (
                      <Text style={
                        item.value === 1 ? pdfStyles.assessmentRed :
                        item.value === 2 ? pdfStyles.assessmentYellow :
                        item.value === 3 ? pdfStyles.assessmentGreen :
                        pdfStyles.assessmentDefault
                      }>
                        {item.value === 1 ? 'No Cumple' :
                         item.value === 2 ? 'Cumple Parcialmente' :
                         item.value === 3 ? 'Cumple' : 'Pendiente'}
                      </Text>
                    )}
                  </View>
                  <Text style={[pdfStyles.tableCell, pdfStyles.comments]}>{item.comments}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.countermeasures]}>
                    {Array.isArray(item.countermeasures) && item.countermeasures.map((measure, idx) => 
                      `${idx + 1}. ${measure}`
                    ).join('\n')}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View style={pdfStyles.footer} fixed>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={pdfStyles.confidential}>CONFIDENCIAL</Text>
          </View>
          <Text 
            style={pdfStyles.pageNumber} 
            render={({ pageNumber, totalPages }) => (
              `${pageNumber} / ${totalPages}`
            )} 
          />
        </View>
      </Page>
    </Document>
  );
};

export default AssessmentReport; 